/**
 * =============================================================================
 * PATIENT JOURNEY - HORIZONTAL PROGRESS BAR
 * =============================================================================
 *
 * Purely presentational. It renders one node per stage declared in
 * `journeyConfig`, coloured by the status the workflow hook computed.
 * =============================================================================
 */

import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { STAGE_STATUS, STAGE_STATUS_LABEL } from "./useCaseWorkflow";
import { FONT, JOURNEY_COLORS } from "./journeyStyles";

/* -----------------------------------------------------------------------------
   NODE COLOURS
----------------------------------------------------------------------------- */

const getNodeStyle = (status) => {
  if (status === STAGE_STATUS.COMPLETED) {
    return {
      background: JOURNEY_COLORS.success,
      border: "2px solid " + JOURNEY_COLORS.success,
      statusColor: JOURNEY_COLORS.success,
    };
  }

  if (status === STAGE_STATUS.ACTIVE) {
    return {
      background: JOURNEY_COLORS.surface,
      border: "2px solid " + JOURNEY_COLORS.accent,
      statusColor: JOURNEY_COLORS.accent,
    };
  }

  if (status === STAGE_STATUS.SKIPPED) {
    return {
      background: JOURNEY_COLORS.surface,
      border: "1px dashed " + JOURNEY_COLORS.line,
      statusColor: JOURNEY_COLORS.textMuted,
    };
  }

  return {
    background: JOURNEY_COLORS.surface,
    border: "1px solid " + JOURNEY_COLORS.line,
    statusColor: JOURNEY_COLORS.textMuted,
  };
};

/**
 * The connector is only drawn as travelled when the case actually moved from
 * this stage to the next one.
 */
const getConnectorColor = (status, nextStatus) => {
  // Both stages completed
  if (
    status === STAGE_STATUS.COMPLETED &&
    nextStatus === STAGE_STATUS.COMPLETED
  ) {
    return JOURNEY_COLORS.success; // Green
  }

  // Current completed and next active
  if (status === STAGE_STATUS.COMPLETED && nextStatus === STAGE_STATUS.ACTIVE) {
    return JOURNEY_COLORS.success; // Green
  }

  return JOURNEY_COLORS.line;
};

/* -----------------------------------------------------------------------------
   COMPONENT
----------------------------------------------------------------------------- */


/**
 * The rail is a row of equal cells, each holding a node and the connector that
 * runs to the next node. The LAST stage has no connector, so leaving it as a
 * full-width cell parked a stage's worth of empty space between the final node
 * and the right edge and the bar never looked like it reached the end. It is
 * sized to its label instead, and every cell before it absorbs what that frees,
 * so the rail spans the full width at any viewport.
 */
const JourneyProgress = ({ stages = [], stageStatuses = [] }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      fontFamily: FONT,
      overflowX: "auto",
      marginBottom: "14px",
      "&::-webkit-scrollbar": {
        height: "4px",
      },
    }}
  >
    {stages.map((stage, index) => {
      const status = stageStatuses[index] || STAGE_STATUS.PENDING;
      const nextStatus = stageStatuses[index + 1];
      const isLast = index === stages.length - 1;

      const nodeStyle = getNodeStyle(status);

      return (
        <Box
          key={stage.id}
          sx={{
            /* The last cell holds a node and a label, nothing to stretch. */
            flex: isLast ? "0 0 auto" : 1,
            /* Narrower floor on phones so more of the rail fits before it
               starts scrolling; roomier once there is width to spend. */
            minWidth: isLast
              ? "auto"
              : { xs: "88px", sm: "104px", md: "120px" },
            maxWidth: isLast ? "132px" : "none",
            position: "relative",
          }}
        >
          {/* ============ NODE + CONNECTOR ============ */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "18px",
            }}
          >
            <Tooltip
              title={STAGE_STATUS_LABEL[status] || ""}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: "18px",
                  height: "18px",
                  minWidth: "18px",
                  borderRadius: "50%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: nodeStyle.background,
                  border: nodeStyle.border,
                  zIndex: 2,
                }}
              >
                {status === STAGE_STATUS.COMPLETED && (
                  <CheckIcon
                    sx={{
                      fontSize: "12px",
                      color: "#FFFFFF",
                    }}
                  />
                )}

                {status === STAGE_STATUS.ACTIVE && (
                  <Box
                    sx={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: JOURNEY_COLORS.accent,
                    }}
                  />
                )}

                {status === STAGE_STATUS.SKIPPED && (
                  <CloseIcon
                    sx={{
                      fontSize: "11px",
                      color: JOURNEY_COLORS.textMuted,
                    }}
                  />
                )}
              </Box>
            </Tooltip>

            {!isLast && (
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  margin: "0 8px",
                  backgroundColor: getConnectorColor(status, nextStatus),
                }}
              />
            )}
          </Box>

          {/* ============ LABELS ============ */}

          <Box
            sx={{
              marginTop: "6px",
              paddingRight: isLast ? 0 : "8px",
              paddingBottom: { xs: "10px", sm: "12px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "10px",
                lineHeight: "12px",
                fontWeight: 600,
                letterSpacing: "0.5px",
                color: JOURNEY_COLORS.textMuted,
                marginBottom: "1px",
              }}
            >
              STEP {index}
            </Typography>

            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: { xs: "12px", sm: "13px" },
                lineHeight: { xs: "14px", sm: "15px" },
                fontWeight: 600,
                color:
                  status === STAGE_STATUS.SKIPPED
                    ? JOURNEY_COLORS.textMuted
                    : JOURNEY_COLORS.text,
                /*
                 * A cell is 88px wide on a phone and "Acknowledgment" is wider
                 * than that with no space to break at, so it used to paint
                 * straight over the next stage's label. Let a single word split
                 * rather than overflow its column.
                 */
                overflowWrap: "anywhere",
                /* Tall enough for the wrapped labels to keep their status lines
                   on one baseline across the row. */
                minHeight: { xs: "42px", sm: "30px" },
              }}
            >
              {stage.label}
            </Typography>

            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: { xs: "11px", sm: "12px" },
                lineHeight: "14px",
                fontWeight: 500,
                color: nodeStyle.statusColor,
                marginTop: "1px",
                overflowWrap: "anywhere",
              }}
            >
              {STAGE_STATUS_LABEL[status]}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default JourneyProgress;
